import React from 'react';
import { Animated, Platform, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import {
  pixelSizeHorizontal,
  pixelSizeVertical,
  widthPixel,
} from '@utils/normalise';

import theme, { getScaledSize, SCALED_DIMENSIONS } from '../../../themes/theme';

/** Figma: the pointer tip sits this far from the element it points at. */
export const TOOLTIP_TARGET_GAP = theme.spacing.xxs;

const POINTER_SIZE = 22;
const POINTER_OFFSET = 10;
/** Corner radius on the pointer's tip (bottom-left pointer's bottom-right). */
const POINTER_TIP_RADIUS = theme.borderRadii.xxs;

/** Native pointer side length (the square rotated 45° into the arrow). */
export const getTooltipPointerSize = () => widthPixel(POINTER_SIZE);

/**
 * TA-10921: distance from a tooltip's edge to the pointer tip, plus the Figma
 * gap. Anchor a tooltip this far from its target so every onboarding arrow
 * ends exactly `TOOLTIP_TARGET_GAP` from what it points at — callers used to
 * hand-tune `bottom` values, which is why each anchor drifted differently.
 */
export const getTooltipAnchorSpacing = (gap = TOOLTIP_TARGET_GAP) => {
  const size = getTooltipPointerSize();
  // Design QA D22: the tip corner is rounded, so the visible tip stops
  // r·(√2 − 1) short of the sharp diagonal — without this the measured gap
  // on Android was ~6px instead of 4px.
  const tipRounding = widthPixel(POINTER_TIP_RADIUS) * (Math.SQRT2 - 1);
  const overhang =
    (size * Math.SQRT2) / 2 -
    size / 2 -
    tipRounding +
    pixelSizeVertical(POINTER_OFFSET);
  return Math.round(overhang + gap);
};

/**
 * Pointer/arrow square's side length (pre-rotation, pre-scale px), mirroring
 * the module's own `POINTER_SIZE`. Exported so callers that need to derive
 * layout around the pointer — e.g. centering a container on the element it
 * points at — share this single source of truth instead of re-hardcoding it
 * (TA-10919 dev review, PR #5939).
 */
export const TOOLTIP_POINTER_SIZE = POINTER_SIZE;
/**
 * How far the pointer's un-rotated box sits past the bubble's own edge
 * (negative = further outside, toward the pointed-at element), mirroring the
 * module's own `POINTER_OFFSET`. Exported for the same reason as
 * `TOOLTIP_POINTER_SIZE`.
 */
export const TOOLTIP_POINTER_OFFSET = -POINTER_OFFSET;

type PointerPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center'
  | 'right-center';

/**
 * Padding (with a matching negative margin) that grows the fade layer past
 * the card edge far enough to contain the overhanging pointer, so the layer's
 * clip doesn't cut it off. +1 absorbs pixel rounding.
 */
const getPointerLayerInset = (pointerPosition: PointerPosition) => {
  const size = getTooltipPointerSize();
  const inset = Math.ceil(
    (size * Math.SQRT2) / 2 - size / 2 + pixelSizeVertical(POINTER_OFFSET) + 1
  );
  switch (pointerPosition) {
    case 'top-left':
    case 'top-right':
      return { paddingTop: inset, marginTop: -inset };
    case 'right-center':
      return { paddingRight: inset, marginRight: -inset };
    default:
      return { paddingBottom: inset, marginBottom: -inset };
  }
};

interface TutorialPopupProps {
  visible: boolean;
  title: string;
  description: string;
  currentStep?: number;
  totalSteps?: number;
  pointerPosition: PointerPosition;
  onSkip?: () => void;
  onClose?: () => void;
  primaryBtn?: () => void;
  skipBtnTitle?: string;
  primaryBtnTitle?: string;
  position?: {
    position?: 'absolute' | 'relative' | 'static';
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  topLeftPos?: number;
  topRightPos?: number;
  bottomLeftPos?: number;
  bottomRightPos?: number;
  animate?: boolean;
  isFromChat?: boolean;
  isFromChatTag?: boolean;
  maxWidth?: number;
  fadeTime?: number;
}

const TooltipPopup: React.FC<TutorialPopupProps> = ({
  visible,
  title,
  description,
  currentStep,
  totalSteps,
  pointerPosition,
  topLeftPos,
  topRightPos,
  bottomLeftPos,
  bottomRightPos,
  onSkip = undefined,
  primaryBtn = undefined,
  onClose = undefined,
  primaryBtnTitle,
  skipBtnTitle,
  position = undefined,
  animate = false,
  isFromChat = false,
  isFromChatTag = false,
  maxWidth = 313,
  fadeTime,
}) => {
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const [shouldRender, setShouldRender] = React.useState<boolean>(false);
  const showTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { tooltipPopupHeight, setTooltipPopupHeight } = useChatTutorialWeb();

  React.useEffect(() => {
    if (!animate) {
      setShouldRender(visible);
      return;
    }

    if (visible) {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }

      fadeAnim.stopAnimation();
      fadeAnim.setValue(0);

      setShouldRender(true);
    } else {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }

      fadeAnim.stopAnimation();

      Animated.timing(fadeAnim, {
        toValue: 0,
        // fadeTime drives the whole dismissal — fading only the pointer
        // left the card vanishing in 200ms regardless (TA-10921 #23).
        duration: fadeTime ?? 200,
        useNativeDriver: true,
      }).start(({ finished }) => {
        // A long fadeTime leaves this running for seconds; if `visible` flips
        // back to true meanwhile, the stopped animation reports
        // finished=false and must not unmount the reopened tooltip.
        if (finished) setShouldRender(false);
      });
    }

    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [visible, animate, fadeAnim, fadeTime]);

  React.useEffect(() => {
    if (!animate || !visible || !shouldRender) return;

    showTimerRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }).start();
      });
    }, 50);

    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [animate, visible, shouldRender, fadeAnim]);

  if (!shouldRender) return null;

  const getPointerStyle = () => {
    const basePointer = {
      position: 'absolute' as const,
      width: Platform.OS === 'web' ? POINTER_SIZE : getTooltipPointerSize(),
      height: Platform.OS === 'web' ? POINTER_SIZE : getTooltipPointerSize(),
      backgroundColor: '#18A87D',
    };
    // Default native inset for the left-side pointers (chat vs. elsewhere).
    const leftPointerInset = pixelSizeHorizontal(isFromChat ? 20 : 43);

    switch (pointerPosition) {
      case 'top-left':
        return {
          ...basePointer,
          top: Platform.OS === 'web' ? -10 : pixelSizeVertical(-10),
          left: Platform.OS === 'web' ? 20 : (topLeftPos ?? leftPointerInset),
          borderTopLeftRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          borderTopRightRadius: Platform.OS === 'web' ? 4 : widthPixel(4),
          borderBottomLeftRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          borderBottomRightRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          transform: [{ rotate: '45deg' }],
        };
      case 'top-right':
        return {
          ...basePointer,
          top: pixelSizeVertical(TOOLTIP_POINTER_OFFSET),
          right: topRightPos ?? pixelSizeHorizontal(isFromChat ? 20 : 40),
          borderTopLeftRadius: widthPixel(4),
          borderTopRightRadius: widthPixel(2),
          borderBottomLeftRadius: widthPixel(2),
          borderBottomRightRadius: widthPixel(2),
          transform: [{ rotate: '45deg' }],
        };
      case 'bottom-left':
        return {
          ...basePointer,
          bottom:
            Platform.OS === 'web'
              ? TOOLTIP_POINTER_OFFSET
              : pixelSizeVertical(TOOLTIP_POINTER_OFFSET),
          left:
            Platform.OS === 'web' ? 20 : (bottomLeftPos ?? leftPointerInset),
          borderBottomLeftRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          borderBottomRightRadius:
            Platform.OS === 'web'
              ? POINTER_TIP_RADIUS
              : widthPixel(POINTER_TIP_RADIUS),
          borderTopLeftRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          borderTopRightRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          transform: [{ rotate: '45deg' }],
        };
      case 'bottom-right':
        return {
          ...basePointer,
          bottom: pixelSizeVertical(TOOLTIP_POINTER_OFFSET),
          right: bottomRightPos ?? pixelSizeHorizontal(isFromChat ? 20 : 40),
          borderBottomLeftRadius: widthPixel(4),
          borderBottomRightRadius: widthPixel(2),
          borderTopLeftRadius: widthPixel(2),
          borderTopRightRadius: widthPixel(2),
          transform: [{ rotate: '45deg' }],
        };
      case 'bottom-center':
        return {
          ...basePointer,
          bottom: pixelSizeVertical(TOOLTIP_POINTER_OFFSET),
          left: bottomRightPos ?? pixelSizeHorizontal((313 - 10) / 2),
          borderBottomLeftRadius: widthPixel(4),
          borderBottomRightRadius: widthPixel(2),
          borderTopLeftRadius: widthPixel(2),
          borderTopRightRadius: widthPixel(2),
          transform: [{ rotate: '45deg' }],
        };
      case 'right-center':
        return {
          ...basePointer,
          right:
            Platform.OS === 'web'
              ? TOOLTIP_POINTER_OFFSET
              : pixelSizeVertical(TOOLTIP_POINTER_OFFSET),
          top:
            tooltipPopupHeight > 0
              ? (tooltipPopupHeight - TOOLTIP_POINTER_SIZE) / 2
              : 20,
          borderBottomLeftRadius: Platform.OS === 'web' ? 4 : widthPixel(4),
          borderBottomRightRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          borderTopLeftRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          borderTopRightRadius: Platform.OS === 'web' ? 2 : widthPixel(2),
          transform: [{ rotate: '45deg' }],
        };
      default:
        return basePointer;
    }
  };

  return (
    // While fading out (fadeTime can run 2-3s) the card must not swallow taps
    // meant for whatever is underneath.
    <Box
      pointerEvents={visible ? 'auto' : 'none'}
      onLayout={(e) => setTooltipPopupHeight(e.nativeEvent.layout.height)}
      style={[
        {
          zIndex: 1000,
          maxWidth: widthPixel(maxWidth),
        },
        position,
      ]}>
      {/* Offscreen compositing applies the fade to card + pointer as one
          layer; otherwise each fades on its own and the pointer's half that
          overlaps the card shows through as a darker diamond mid-fade. The
          layer clips to this view's bounds on Android, so it is padded on the
          pointer side (and pulled back by the same negative margin) to keep
          the overhanging pointer inside it. Gated on `animate` so static
          tooltips keep the default (cheaper) rendering path. */}
      <Animated.View
        needsOffscreenAlphaCompositing={animate}
        renderToHardwareTextureAndroid={animate}
        style={[
          { opacity: animate ? fadeAnim : 1 },
          animate && getPointerLayerInset(pointerPosition),
        ]}>
        {/* Main popup container */}
        <Box
          borderRadius='s'
          position='relative'
          style={{
            backgroundColor: '#18A87D',
            paddingHorizontal:
              Platform.OS === 'web' ? 16 : pixelSizeHorizontal(16),
            paddingVertical: Platform.OS === 'web' ? 16 : pixelSizeVertical(16),
            marginTop:
              isFromChatTag && Platform.OS === 'ios'
                ? pixelSizeVertical(8)
                : undefined,
          }}>
          {/* Pointer/Arrow — faded by the layer above, together with the
              card; its own opacity would make it fade faster than the card. */}
          <Box style={getPointerStyle()} />

          {/* Content */}
          <Box>
            {/* Header: Title left, Close icon right */}
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              marginBottom='xxs'
              paddingBottom='xxxs'>
              <Text
                variant='labelEmphasized'
                color='white'
                fontSize={16}
                fontWeight='600'
                lineHeight={21}>
                {title}
              </Text>
              {onClose && (
                <TouchableOpacity onPress={onClose}>
                  <Icon variant='l' color='black' name='X' />
                </TouchableOpacity>
              )}
            </Box>

            <Text
              variant='bodySecondary'
              color='white'
              fontSize={14}
              fontWeight='400'
              lineHeight={16}>
              {description}
            </Text>
          </Box>

          {/* Step indicator */}
          <Box
            flexDirection='row'
            justifyContent='space-between'
            marginTop={currentStep && totalSteps ? 'm' : undefined}>
            {currentStep && totalSteps && (
              <Text
                paddingVertical='xs'
                variant='bodySecondary'
                color='grey01'
                fontSize={14}
                fontWeight='400'
                lineHeight={16}>
                Step {currentStep}/{totalSteps}
              </Text>
            )}
            {onSkip && (
              <TouchableOpacity onPress={onSkip} activeOpacity={0.7}>
                <Box
                  backgroundColor='black'
                  paddingHorizontal='s'
                  height={28}
                  borderRadius='m'
                  justifyContent='center'
                  alignItems='center'>
                  <Text fontSize={16} fontWeight='500' color='white'>
                    {skipBtnTitle ?? 'Skip'}
                  </Text>
                </Box>
              </TouchableOpacity>
            )}
          </Box>
          {primaryBtn && (
            <Box marginTop='s' alignSelf='flex-end'>
              <TouchableOpacity onPress={primaryBtn}>
                <Box
                  backgroundColor='black'
                  height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                  borderRadius='m'
                  paddingHorizontal='m'
                  justifyContent='center'>
                  <Text
                    fontSize={16}
                    fontWeight='500'
                    color='white'
                    textAlign='center'>
                    {primaryBtnTitle}
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>
          )}
        </Box>
      </Animated.View>
    </Box>
  );
};

export default TooltipPopup;
